import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useState } from "react";
import { KeyRoundIcon } from "lucide-react";
import { ArForm } from "../../ui/form";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
    UpdatePasswordSchema,
    UpdatePasswordSchemaWithoutRefine,
} from "@/v2/schema/members";
import { ArPasswordFormField } from "../../ui/form/PasswordFormField";
import { useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import UpdatePassword from "@/v2/api/users/UpdatePassword";

export default function ChangePassword({ id }: { id: string }) {
    const [open, setOpen] = useState(false);

    if (!id) {
        setOpen(false);
    }

    const form = useForm<UpdatePasswordSchema>({
        resolver: zodResolver(UpdatePasswordSchema),
        defaultValues: {
            password: "",
            confirmPassword: "",
        },
    });

    const Mutation = useMutation({
        mutationFn: (data: UpdatePasswordSchema) =>
            UpdatePassword({ id: id!, data }),
    });

    const handleFormSubmit = (data: UpdatePasswordSchema) => {
        Mutation.mutate(data, {
            onError: (err) => {
                console.error({ err });
                toast.error("Something went wrong", {
                    description: `While updating the user password, an issue occurred.`,
                });
            },
            onSuccess: () => {
                toast.success(`User password successfully updated`);
                setOpen(false);
            },
        });
    };

    return (
        <Dialog open={open} onOpenChange={(value) => setOpen(value)}>
            <DialogTrigger asChild>
                <Button size={"sm"} variant={"outline"} className="flex gap-2">
                    <KeyRoundIcon />
                    Change Password
                </Button>
            </DialogTrigger>
            <DialogContent className="min-w-[600px]">
                <DialogHeader>
                    <DialogTitle className="text-2xl font-semibold tracking-tight">
                        Change user password
                    </DialogTitle>
                    <DialogDescription className="text-sm text-muted-foreground">
                        Fill in the details to change user password
                    </DialogDescription>
                </DialogHeader>
                <ArForm
                    form={form}
                    formSchema={UpdatePasswordSchemaWithoutRefine}
                    onSubmit={handleFormSubmit}
                    className="flex flex-col gap-3"
                >
                    <ArPasswordFormField<UpdatePasswordSchema>
                        label="Password"
                        name="password"
                        placeholder="Enter password"
                        infoText="Must be at least 8 characters, include uppercase, lowercase, number, and special character."
                    />

                    <ArPasswordFormField<UpdatePasswordSchema>
                        label="Confirm Password"
                        name="confirmPassword"
                        placeholder="Confirm password"
                        infoText="Re enter the same passowrd to confirm"
                    />
                    <div className="flex justify-end gap-2 border-t pt-4">
                        <Button
                            type="submit"
                            className="min-w-[160px]"
                            disabled={Mutation.isPending}
                        >
                            Update Password
                        </Button>
                    </div>
                </ArForm>
            </DialogContent>
        </Dialog>
    );
}
